import { useMemo } from 'react'

import RecommendationDisplayWrapper, {
  RecommendationDisplayWrapperMetadata,
  wrapRecommendationRegistry,
} from '$extensionSrc/recommendations/recommendationDisplayWrapper'

import { useLoginData } from './loginData'
import { getRecommendationsRegistryForPlatform } from '../recommendations/recommendationRegistryApi'
import { RecommendationRegistry } from '../recommendations/recommendationTypes'
import { PLATFORM, RecommendationKey } from '../utils/enums'
import { useGoogleActiveAccount } from './sidebar/googleActiveAccount'

export type RecommendationSpecs = RecommendationRegistry
export type RecommendationSpecsWrapped = Partial<
  Record<RecommendationKey, RecommendationDisplayWrapper>
>

export function getSpec(
  recommendationKey: RecommendationKey,
  recommendationSpecs: RecommendationSpecsWrapped,
): RecommendationDisplayWrapper {
  const spec = recommendationSpecs[recommendationKey]
  if (!spec) {
    throw new Error(
      `useFilteredRecommendationKeys: Could not find spec for ${recommendationKey}`,
    )
  }
  return spec
}

// @TODO: Move this file to data/platform since it accepts a platform param and
// returns platform-specific specs. It is just like data/accountData (account
// data for all platforms) vs. data/platform/platformUserAccountData (account
// data that is specific to a platform and platform user ID.
export function getRecommendationSpecs(
  platform: PLATFORM | null | undefined,
): RecommendationSpecs | undefined {
  if (platform === undefined) {
    return undefined
  }
  if (platform === null) {
    return {}
  }
  return getRecommendationsRegistryForPlatform(platform)
}

export function useRecommendationSpecsWrapped(
  platform: PLATFORM | null | undefined,
):
  | Partial<Record<RecommendationKey, RecommendationDisplayWrapper>>
  | undefined {
  const googleActiveAccount = useGoogleActiveAccount()
  const loginData = useLoginData()

  return useMemo(() => {
    const unwrappedRecommendationSpecs = getRecommendationSpecs(platform)

    if (unwrappedRecommendationSpecs === undefined) {
      return undefined
    }

    if (platform === PLATFORM.GOOGLE && googleActiveAccount && loginData) {
      const platformAccountId = loginData?.[PLATFORM.GOOGLE]
      const googleAccountIndex =
        googleActiveAccount.knownAccounts?.[platformAccountId]?.accountIndex
      if (googleAccountIndex) {
        const wrapMetadata: RecommendationDisplayWrapperMetadata = {
          googleAccountIndex,
        }
        return wrapRecommendationRegistry(
          unwrappedRecommendationSpecs,
          wrapMetadata,
        )
      }
    }

    return wrapRecommendationRegistry(unwrappedRecommendationSpecs)
  }, [platform, googleActiveAccount, loginData])
}
